import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Search, X } from 'lucide-react';
import { useMilestoneTypes } from '@/hooks/milestones/useMilestoneTypes';

interface TimelineReportMilestoneFilterProps {
  activeMilestoneTypes: string[];
  milestoneName: string;
  onChange: (filters: { activeMilestoneTypes?: string[]; milestoneName?: string }) => void;
}

export default function TimelineReportMilestoneFilter({
  activeMilestoneTypes,
  milestoneName,
  onChange
}: TimelineReportMilestoneFilterProps) {
  const { data: milestoneTypes = [] } = useMilestoneTypes();

  return (
    <div className="space-y-3 p-4 bg-muted/30 rounded-lg">
      <div className="text-sm font-medium">Filter Milestones (Preview)</div>
      
      {/* Milestone Type Filter */}
      <div className="flex flex-wrap gap-2 items-center">
        <span className="text-xs text-muted-foreground">Types:</span>
        
        <Button
          variant="outline"
          size="sm"
          onClick={() => onChange({ 
            activeMilestoneTypes: milestoneTypes.map(t => t.name),
            milestoneName 
          })}
          className="text-xs h-7"
        >
          Show All
        </Button>
        
        <Button
          variant="outline"
          size="sm"
          onClick={() => onChange({ 
            activeMilestoneTypes: [],
            milestoneName 
          })}
          className="text-xs h-7"
        >
          Hide All
        </Button>
        
        {milestoneTypes.map((type) => {
          const isActive = activeMilestoneTypes.includes(type.name);
          return (
            <Badge
              key={type.name}
              variant={isActive ? 'default' : 'outline'}
              className="cursor-pointer text-xs flex items-center gap-1.5"
              onClick={() => {
                const newTypes = isActive
                  ? activeMilestoneTypes.filter(t => t !== type.name)
                  : [...activeMilestoneTypes, type.name];
                onChange({ activeMilestoneTypes: newTypes, milestoneName });
              }}
            >
              <div
                className="w-2.5 h-2.5 rounded-full border border-white/30"
                style={{ backgroundColor: type.color }}
              />
              <span>{type.display_name}</span>
              {isActive && <span className="ml-0.5">✓</span>}
            </Badge>
          );
        })}
        
        {activeMilestoneTypes.length > 0 && (
          <Badge variant="secondary" className="text-xs">
            {activeMilestoneTypes.length} active
          </Badge>
        )}
      </div>
      
      {/* Milestone Name Search */}
      <div className="flex items-center gap-2">
        <span className="text-xs text-muted-foreground whitespace-nowrap">
          Search:
        </span>
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="e.g., Phase 1, Foundation Pour"
            value={milestoneName}
            onChange={(e) => onChange({ 
              activeMilestoneTypes,
              milestoneName: e.target.value.slice(0, 100)
            })}
            className="pl-9 pr-9 h-9 text-sm"
            maxLength={100}
          />
          {milestoneName && (
            <Button
              variant="ghost"
              size="sm"
              className="absolute right-1 top-1/2 transform -translate-y-1/2 px-2 h-7"
              onClick={() => onChange({ activeMilestoneTypes, milestoneName: '' })}
            >
              <X className="h-3 w-3" />
            </Button>
          )}
        </div>
        
        {milestoneName && (
          <Badge variant="outline" className="text-xs whitespace-nowrap">
            Filtering: "{milestoneName.trim()}"
          </Badge>
        )}
      </div>
    </div>
  );
}
